import type { RequestFn } from '../http';
import type { ContactResponsibility } from './customerCare';
import type { PortalAccessOptions, PortalAreaValue } from '../../portalAccessTypes';

export type TechnicianWorkflowKind =
  | 'QUOTE_REQUEST'
  | 'WARRANTY_CHECK'
  | 'MATERIAL_ORDER'
  | 'CUSTOMER_ORDER';

export type CustomerOrderCategory =
  | 'edv_hardware'
  | 'drucker_toner'
  | 'lizenzen_software'
  | 'netzwerk_peripherie'
  | 'sonstiges';

export type CustomerOrderPriority = 'normal' | 'hoch' | 'kritisch';

export interface CustomerOrderLine {
  description: string;
  qty: number;
  unitPriceEur?: number;
  productUrl?: string;
  manufacturerSku?: string;
}

export interface CustomerOrderAuthorizer {
  name: string;
  email: string;
  role: string;
  authorizedAt: string;
  sendConfirmationEmail: boolean;
}

export type TechnicianWorkflowStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'PENDING_CUSTOMER_APPROVAL'
  | 'CUSTOMER_APPROVED'
  | 'CW_TICKET_CREATED'
  | 'FAILED';

export type HubProvider = 'connectwise' | 'weclapp';

export interface TechnicianWorkflow {
  id: string;
  userId: string;
  kind: TechnicianWorkflowKind;
  status: TechnicianWorkflowStatus;
  schemaVersion: number;
  payload: Record<string, unknown>;
  customerId: string | null;
  cwCompanyId: number | null;
  cwCompanyName: string | null;
  cwSiteId: number | null;
  cwContactId: number | null;
  cwTicketId: number | null;
  referenceTicketId: number | null;
  weclappCustomerId: string | null;
  weclappCustomerName: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Adress-Typ-Marker eines weclapp-Standorts (mehrere gleichzeitig möglich). */
export type HubAddressTag = 'PRIME' | 'INVOICE' | 'DELIVERY';

export interface HubContextSite {
  id: number;
  name: string;
  /** Nur im weclapp-Modus gesetzt: Haupt-/Rechnungs-/Lieferanschrift-Tags. */
  addressType?: HubAddressTag[];
  street?: string | null;
  zip?: string | null;
  city?: string | null;
}

export interface HubCustomerContext {
  company: { id: number; name: string };
  sites: HubContextSite[];
  contacts: {
    id: number;
    name: string;
    /** Echte Weclapp-Kontakt-Id (im Weclapp-Modus ist `id` nur der Array-Index). */
    weclappContactId?: string;
    email?: string | null;
    /** Portal-interne Zuständigkeiten — Haupt-AP + Themen (Angebote, Rechnungen, …). */
    responsibilities?: ContactResponsibility[];
    isPrimary?: boolean;
  }[];
  /** Kundennummer (Portal-Datensatz bzw. weclapp-Party) — steht in der Hub-Kopfzeile neben dem Namen. */
  customerNumber?: string | null;
  weclappCustomerId: string | null;
  weclappCustomerName: string | null;
  referenceTicket: {
    id: number;
    summary: string;
    boardId?: number;
    statusId?: number;
    priorityId?: number;
  } | null;
  localCustomer?: {
    id: string;
    customerNumber: string | null;
    cwCompanyId: number | null;
  };
}

export interface HubAttachment {
  filename: string;
  mimeType: string;
  contentBase64: string;
}

export interface HubSubmitResult {
  ticketId: string;
  ticketLink?: string;
  provider: HubProvider;
  /** Nur bei ConnectWise gesetzt */
  cwTicketId?: number;
  /** Nur bei Weclapp gesetzt */
  weclappTicketId?: string;
}

export interface HubCustomer {
  id: string;
  name: string;
  customerNumber?: string | null;
  city?: string | null;
  cwCompanyId: number | null;
  weclappCustomerId?: string | null;
}

export function createTechnicianHubApi(request: RequestFn) {
  return {
    async getHubProvider() {
      return request<{ provider: HubProvider; externalUiBase: string | null }>('/technician-hub/provider');
    },

    async getRecentHubCustomers() {
      return request<{ customers: Array<{ id: string; name: string; isWeclapp: boolean; customerNumber?: string | null; city?: string | null }> }>(
        '/technician-hub/recent-customers'
      );
    },

    async addRecentHubCustomer(
      id: string,
      name: string,
      isWeclapp: boolean,
      extra?: { customerNumber?: string | null; city?: string | null }
    ) {
      return request<{ customers: Array<{ id: string; name: string; isWeclapp: boolean; customerNumber?: string | null; city?: string | null }> }>(
        '/technician-hub/recent-customers',
        { method: 'POST', body: JSON.stringify({ id, name, isWeclapp, ...extra }) }
      );
    },

    async searchHubCustomers(q: string, page = 1, pageSize = 25) {
      const params = new URLSearchParams({ q, page: String(page), pageSize: String(pageSize) });
      return request<{ customers: HubCustomer[]; hasMore: boolean; total: number }>(
        `/technician-hub/search-customers?${params}`
      );
    },

    async getTechnicianHubCustomerContext(
      cwCompanyId?: number,
      cwTicketId?: number,
      weclappCustomerId?: string,
      customerId?: string
    ) {
      const q = new URLSearchParams();
      if (customerId) q.set('customerId', customerId);
      else if (cwCompanyId != null) q.set('cwCompanyId', String(cwCompanyId));
      else if (weclappCustomerId) q.set('weclappCustomerId', weclappCustomerId);
      if (cwTicketId != null) q.set('cwTicketId', String(cwTicketId));
      return request<{ context: HubCustomerContext }>(`/technician-hub/customer-context?${q}`);
    },

    /**
     * Portal-Sicht: was dieser Kunde im Kundenportal hat und sieht.
     * `view: null` = keine interne Kundenakte, also (noch) kein Portal.
     */
    async getCustomerPortalView(params: {
      customerId?: string;
      cwCompanyId?: number;
      weclappCustomerId?: string;
    }) {
      const q = new URLSearchParams();
      if (params.customerId) q.set('customerId', params.customerId);
      else if (params.cwCompanyId != null) q.set('cwCompanyId', String(params.cwCompanyId));
      else if (params.weclappCustomerId) q.set('weclappCustomerId', params.weclappCustomerId);
      return request<{ view: CustomerPortalView | null }>(`/technician-hub/portal-view?${q}`);
    },

    /** Register, Vorlagen und Grenzen für „Zugriff im Portal" — für alle Dialoge, die Portal-Rechte vergeben. */
    async getPortalAccessOptions() {
      return request<PortalAccessOptions>('/technician-hub/portal-access/options');
    },

    /** Portal-Zugang aus der Portal-Sicht freischalten — die Rechte-Staffelung prüft der Server. */
    async grantCustomerPortalAccess(contactId: string, body: PortalAreaValue & { sendInvite: boolean }) {
      return request<{
        contactId: string;
        inviteSent: boolean;
        inviteError: string | null;
        customerPortalEnabled: boolean;
      }>(`/technician-hub/portal-access/${encodeURIComponent(contactId)}`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    /** Einladungsmail ansehen, bevor sie rausgeht — erzeugt keinen Link, verschickt nichts. */
    async previewCustomerPortalInvite(contactId: string) {
      return request<PortalInvitePreview>(
        `/technician-hub/portal-access/${encodeURIComponent(contactId)}/invite-preview`
      );
    },

    /**
     * Einladung an jemanden mit Portal-Zugang erneut schicken — neuer Anmelde-Link samt Anleitung für
     * Microsoft 365 und Google. Frühere Einladungslinks aus dem Kunden-Hub verfallen.
     */
    async resendCustomerPortalInvite(contactId: string) {
      return request<{ contactId: string; sentTo: string; revokedPrevious: number; customerPortalEnabled: boolean }>(
        `/technician-hub/portal-access/${encodeURIComponent(contactId)}/invite`,
        { method: 'POST', body: JSON.stringify({}) }
      );
    },

    /** Konzern dieses Kunden; `customer: null` = keine interne Kundenakte */
    async getCustomerGroupForCustomer(params: {
      customerId?: string;
      cwCompanyId?: number;
      weclappCustomerId?: string;
    }) {
      const q = new URLSearchParams();
      if (params.customerId) q.set('customerId', params.customerId);
      else if (params.cwCompanyId != null) q.set('cwCompanyId', String(params.cwCompanyId));
      else if (params.weclappCustomerId) q.set('weclappCustomerId', params.weclappCustomerId);
      return request<{ customer: { id: string; name: string } | null; group: HubCustomerGroup | null }>(
        `/technician-hub/customer-group?${q}`
      );
    },

    async createCustomerGroupForCustomer(body: { customerId: string; name: string }) {
      return request<{ group: HubCustomerGroup }>('/technician-hub/customer-groups', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async renameHubCustomerGroup(groupId: string, body: { name: string }) {
      return request<{ group: HubCustomerGroup }>(`/technician-hub/customer-groups/${groupId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },

    /** `confirmName`: eingetippter Firmenname — das Backend prüft ihn gegen die Firma */
    async attachCompanyToCustomerGroup(groupId: string, customerId: string, confirmName: string) {
      return request<{ group: HubCustomerGroup }>(`/technician-hub/customer-groups/${groupId}/members`, {
        method: 'POST',
        body: JSON.stringify({ customerId, confirmName }),
      });
    },

    async detachCompanyFromCustomerGroup(groupId: string, customerId: string) {
      return request<{ group: HubCustomerGroup; ineffectiveGrants: number }>(
        `/technician-hub/customer-groups/${groupId}/members/${customerId}`,
        { method: 'DELETE' }
      );
    },

    async createTechnicianWorkflow(body: {
      kind: TechnicianWorkflowKind;
      customerId?: string;
      cwCompanyId?: number;
      cwCompanyName?: string;
      cwSiteId?: number;
      cwContactId?: number;
      weclappCustomerId?: string;
      weclappCustomerName?: string;
      referenceTicketId?: number;
      payload?: Record<string, unknown>;
    }) {
      return request<{ workflow: TechnicianWorkflow }>('/technician-hub/workflows', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async patchTechnicianWorkflow(id: string, payload: Record<string, unknown>) {
      return request<{ workflow: TechnicianWorkflow }>(`/technician-hub/workflows/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ payload }),
      });
    },

    async getTechnicianWorkflow(id: string) {
      return request<{ workflow: TechnicianWorkflow; smallOrderMaxEur: number }>(`/technician-hub/workflows/${id}`);
    },

    async listTechnicianWorkflows(limit = 20, cwCompanyId?: number, weclappCustomerId?: string) {
      const params = new URLSearchParams({ limit: String(limit) });
      if (cwCompanyId != null) params.set('cwCompanyId', String(cwCompanyId));
      if (weclappCustomerId) params.set('weclappCustomerId', weclappCustomerId);
      return request<{ workflows: TechnicianWorkflow[] }>(
        `/technician-hub/workflows?${params}`
      );
    },

    async submitTechnicianHubQuote(
      id: string,
      body: {
        summary: string;
        description: string;
        urgency?: 'normal' | 'hoch' | 'kritisch';
        referenceTicketId?: number;
        cwSiteId?: number;
        cwContactId?: number;
        attachments: HubAttachment[];
      }
    ) {
      return request<HubSubmitResult>(
        `/technician-hub/workflows/${id}/submit-quote`,
        { method: 'POST', body: JSON.stringify(body) }
      );
    },

    async submitTechnicianHubWarranty(
      id: string,
      body: {
        deviceLabel: string;
        serialNumber: string;
        symptom: string;
        purchaseDate?: string;
        attachments: HubAttachment[];
      }
    ) {
      return request<HubSubmitResult>(
        `/technician-hub/workflows/${id}/submit-warranty`,
        { method: 'POST', body: JSON.stringify(body) }
      );
    },

    async submitTechnicianHubOrder(
      id: string,
      body: {
        orderCategory: 'small_order' | 'recurring' | 'express';
        lines: { description: string; qty: number; unitPriceEur?: number }[];
        fulfillmentNotes?: string;
        remoteApproval?: boolean;
        onSiteApproverName?: string;
        recurringApproverName?: string;
        onSiteSignaturePngBase64?: string;
        requestedDeliveryDate?: string;
        attachments: HubAttachment[];
      }
    ) {
      return request<HubSubmitResult & {
        pendingApproval: boolean;
        approvalUrl?: string;
        approvalExpiresAt?: string;
      }>(`/technician-hub/workflows/${id}/submit-order`, { method: 'POST', body: JSON.stringify(body) });
    },

    async submitTechnicianHubCustomerOrder(
      id: string,
      body: {
        category: CustomerOrderCategory;
        priority: CustomerOrderPriority;
        neededByDate?: string;
        cwSiteId?: number;
        cwContactId?: number;
        lines: CustomerOrderLine[];
        authorizer: CustomerOrderAuthorizer;
        fulfillmentNotes?: string;
        attachments: HubAttachment[];
      }
    ) {
      return request<
        HubSubmitResult & {
          confirmationEmailSent: boolean;
          confirmationEmailError?: string;
        }
      >(`/technician-hub/workflows/${id}/submit-customer-order`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async suggestTechnicianHubPrefill(body: { freeText: string; kind: TechnicianWorkflowKind }) {
      return request<{ suggestion: Record<string, unknown> }>(
        '/technician-hub/suggest-prefill',
        { method: 'POST', body: JSON.stringify(body) }
      );
    },

    async parseTechnicianHubOrderLines(body: { freeText: string }) {
      return request<{ lines: { description: string; qty: number; unitPriceEur?: number }[] }>(
        '/technician-hub/parse-order-lines',
        { method: 'POST', body: JSON.stringify(body) }
      );
    },

    async createTechnicianHubCustomerApprovalLink(id: string) {
      return request<{ approvalUrl: string; approvalExpiresAt: string }>(
        `/technician-hub/workflows/${id}/customer-approval-link`,
        { method: 'POST', body: JSON.stringify({}) }
      );
    },

    // ── Outlook-Kalender-Anbindung ──────────────────────────────────────────────

    async getCalendarStatus() {
      return request<{ connected: boolean; connectedAt: string | null }>(
        '/technician-hub/calendar/status'
      );
    },

    async startCalendarConnect() {
      return request<{
        userCode: string;
        verificationUri: string;
        verificationUriComplete: string;
        expiresIn: number;
        interval: number;
        deviceCode: string;
      }>('/technician-hub/calendar/connect/start', { method: 'POST', body: JSON.stringify({}) });
    },

    async pollCalendarConnect(deviceCode: string) {
      return request<{ status: 'pending' | 'connected' | 'error'; message?: string }>(
        '/technician-hub/calendar/connect/poll',
        { method: 'POST', body: JSON.stringify({ deviceCode }) }
      );
    },

    async disconnectCalendar() {
      return request<{ ok: boolean }>('/technician-hub/calendar/connect', { method: 'DELETE' });
    },

    async getCalendarEvents(date: string) {
      return request<{ events: Array<{ id: string; subject: string; start: string; end: string }> }>(
        `/technician-hub/calendar/events?date=${encodeURIComponent(date)}`
      );
    },

    async getCustomerAddress(customerId: string) {
      return request<{ address: string | null }>(
        `/technician-hub/customer-address?customerId=${encodeURIComponent(customerId)}`
      );
    },

    // ── Portal-Terminplanung ────────────────────────────────────────────────────

    async getPortalAppointments(params?: { customerId?: string; status?: string; from?: string; to?: string }) {
      const q = new URLSearchParams();
      if (params?.customerId) q.set('customerId', params.customerId);
      if (params?.status) q.set('status', params.status);
      if (params?.from) q.set('from', params.from);
      if (params?.to) q.set('to', params.to);
      return request<{ appointments: PortalAppointment[] }>(`/technician-hub/appointments?${q}`);
    },

    async createPortalAppointment(body: {
      customerId: string;
      tickets?: AppointmentTicketInput[];
      scheduledAt: string;
      durationMin?: number;
      location?: string;
      notes?: string;
      confirmed?: boolean;
    }) {
      return request<{ appointment: PortalAppointment }>('/technician-hub/appointments', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async patchPortalAppointment(
      id: string,
      body: {
        action: 'confirm' | 'cancel' | 'reschedule';
        scheduledAt?: string;
        durationMin?: number;
        location?: string;
        notes?: string;
      }
    ) {
      return request<{ appointment: PortalAppointment }>(`/technician-hub/appointments/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
  };
}

export interface AppointmentTicketInput {
  weclappTicketId?: string;
  ticketNumber?: string;
  subject?: string;
}

export interface AppointmentTicket {
  id: string;
  weclappTicketId: string | null;
  ticketNumber: string | null;
  subject: string | null;
}

export type PortalAppointmentStatus =
  | 'PROPOSED' | 'CONFIRMED' | 'ACCEPTED' | 'DECLINED' | 'CANCELLED' | 'RESCHEDULED';

export interface PortalAppointment {
  id: string;
  customerId: string;
  customer?: { id: string; name: string };
  tickets: AppointmentTicket[];
  /** @deprecated Aus erstem tickets-Eintrag – für Backward-Compat */
  cwTicketId: number | null;
  /** @deprecated Aus erstem tickets-Eintrag – für Backward-Compat */
  ticketSummary: string | null;
  technicianName: string;
  status: PortalAppointmentStatus;
  scheduledAt: string;
  durationMin: number;
  location: string | null;
  notes: string | null;
  respondedAt: string | null;
  responseNote: string | null;
  originalId: string | null;
  createdAt: string;
  updatedAt: string;
}


// ─── Portal-Sicht (Techniker schaut, was der Kunde im Portal hat) ─────────────

export type PortalViewModuleId =
  | 'tickets' | 'requests' | 'inbox' | 'shop' | 'quotes' | 'submissions'
  | 'reports' | 'assets' | 'contracts' | 'appointments' | 'projects' | 'trust' | 'audit' | 'dns';

export interface PortalViewCompany {
  id: string;
  name: string;
  customerNumber: string | null;
  portalAccessEnabled: boolean;
  enabledModules: PortalViewModuleId[];
  isCurrent: boolean;
}

/** Einladungsmail vor dem Versand — beim Freischalten und bei „Einladung erneut senden" */
export interface PortalInvitePreview {
  subject: string;
  html: string;
  to: string;
  expiresMinutes: number;
  /** Firmenkonto-Anmeldung, die die Mail erklärt — leer: nicht eingerichtet, die Anleitung fehlt */
  ssoProviders: Array<'microsoft' | 'google'>;
}

export interface PortalViewContact {
  id: string;
  name: string;
  email: string | null;
  role: string;
  /** E-Mail-Domain eindeutig diesem Kunden zugeordnet — dann darf jeder Techniker die Einladung erneut senden */
  domainAssigned: boolean;
  /** Bereiche, die die Person bei der eigenen Gesellschaft tatsächlich öffnen kann */
  areas: PortalViewModuleId[];
  canSeeDeadlines: boolean;
  activeSessions: number;
  companies: Array<{ id: string; name: string; isHome: boolean; modules: PortalViewModuleId[] }>;
  /** Haupt-Ansprechpartner (Zuständigkeit PRIMARY) — steht oben */
  isPrimary: boolean;
  /** Geschäftsführung (Zuständigkeit oder Position) — steht nach den Haupt-APs */
  isManagement: boolean;
}

/** Portal-Rolle eines Ansprechpartners */
export type PortalContactRole = 'ADMIN' | 'MITARBEITER' | 'GAST';

/** Ansprechpartner ohne Portal-Zugang — freischaltbar aus der Portal-Sicht */
export interface PortalViewCandidate {
  id: string;
  name: string;
  email: string | null;
  role: string;
  /** E-Mail-Domain eindeutig diesem Kunden zugeordnet — dann darf jeder Techniker freischalten */
  domainAssigned: boolean;
  /** Dieselbe Adresse hat schon Portal-Zugang bei diesem Kunden (Name) */
  accessElsewhere: string | null;
  /** Schwestergesellschaften, die schon vor dem Freischalten freigegeben sind — wirken ab dem ersten Login */
  groupAccess: Array<{ id: string; name: string }>;
  isPrimary: boolean;
  isManagement: boolean;
}

export interface PortalViewIncomingAccess {
  contactId: string;
  contactName: string;
  contactEmail: string | null;
  homeCustomerName: string;
  effective: boolean;
  reason: string | null;
  modules: PortalViewModuleId[];
}

export interface PortalViewDeadline {
  id: string;
  customerId: string;
  companyName: string;
  visibleToCustomer: boolean;
  category: string;
  ownership: 'FIXBYTE' | 'CUSTOMER_MANAGED';
  name: string;
  provider: string | null;
  reference: string | null;
  endDate: string;
  termEndDate: string;
  autoRenew: boolean;
  renewalMonths: number | null;
  noticeDays: number | null;
  noticeAnchor: 'END_DATE' | 'MONTH_END';
  terminationPlanned: boolean;
  customerNote: string | null;
  createdByCustomer: boolean;
  status: 'EXPIRED' | 'CRITICAL' | 'WARNING' | 'RENEWING' | 'ENDING' | 'OK';
  actionDate: string;
  daysLeft: number;
  actionIsCancellation: boolean;
}

/** Managed Service am FixByte-Servicevertrag, wie ihn der Kunde sieht — ohne Frist und Ampel. */
export interface PortalViewService {
  id: string;
  customerId: string;
  companyName: string;
  visibleToCustomer: boolean;
  category: string;
  ownership: 'FIXBYTE' | 'CUSTOMER_MANAGED';
  name: string;
  provider: string | null;
  reference: string | null;
  customerNote: string | null;
  termType: 'SERVICE_CONTRACT' | 'PENDING';
}

export interface CustomerPortalView {
  customer: {
    id: string;
    name: string;
    customerNumber: string | null;
    portalAccessEnabled: boolean;
    modulesConfigured: boolean;
    enabledModules: PortalViewModuleId[];
  };
  group: { id: string; name: string; companies: PortalViewCompany[] } | null;
  contacts: PortalViewContact[];
  contactsWithoutAccess: PortalViewCandidate[];
  incomingAccess: PortalViewIncomingAccess[];
  deadlines: PortalViewDeadline[];
  services: PortalViewService[];
  pendingTerms: PortalViewService[];
}


// ─── Konzern im Kunden-Hub ────────────────────────────────────────────────────

export interface HubCustomerGroup {
  id: string;
  name: string;
  note: string | null;
  companies: Array<{
    id: string;
    name: string;
    customerNumber: string | null;
    portalAccessEnabled: boolean;
  }>;
}
